import { makeStyles } from '@material-ui/core/styles'
import {
  getOutlinedSelectWrapperStyles,
  getOutlinedTextFieldWrapperStyles,
  OUTLINED_INPUT_BORDER,
  OUTLINED_INPUT_BORDER_HOVER,
  OUTLINED_INPUT_BORDER_RADIUS,
} from 'lib/form-field-styles'

export const useCreateProfileFieldStyles = makeStyles(theme => ({
  fieldStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
    width: '100%',
    '& > .MuiTextField-root, & > .MuiAutocomplete-root': {
      marginBottom: 0,
    },
    '& .MuiFormControl-root': {
      marginBottom: 0,
      marginTop: 0,
    },
    '& .MuiFormHelperText-root': {
      display: 'none',
    },
  },
  sectionStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    width: '100%',
  },
  fieldGroupStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    width: '100%',
  },
  tipFieldStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    width: '100%',
  },
  fieldSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: '100%',
  },
  fieldInput: {
    maxWidth: '100%',
    width: '100%',
    marginBottom: 0,
    ...getOutlinedTextFieldWrapperStyles(theme),
    '& .MuiFormHelperText-root': {
      display: 'none',
    },
  },
  selectInput: {
    maxWidth: '100%',
    width: '100%',
    marginBottom: 0,
    ...getOutlinedTextFieldWrapperStyles(theme),
    ...getOutlinedSelectWrapperStyles(theme),
    '& .MuiFormHelperText-root': {
      display: 'none',
    },
  },
  fieldGroup: {
    marginBottom: theme.spacing(3),
    width: '100%',
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(2),
    width: '100%',
    marginBottom: 0,
    '& > *': {
      minWidth: 0,
    },
    '& $fieldInput, & $selectInput': {
      marginBottom: 0,
    },
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  contactPhoneRow: {
    display: 'grid',
    gridTemplateColumns:
      'minmax(0, 1.5fr) minmax(72px, 0.75fr) minmax(0, 1.5fr)',
    gap: theme.spacing(1.5),
    alignItems: 'flex-start',
    width: '100%',
    marginBottom: 0,
    '& > *': {
      minWidth: 0,
    },
    '& $fieldInput': {
      marginBottom: 0,
    },
    '& .MuiFormControl-root': {
      marginBottom: 0,
      marginTop: 0,
    },
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  locationRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr) minmax(96px, 0.75fr)',
    gap: theme.spacing(2, 1.5),
    alignItems: 'flex-start',
    width: '100%',
    marginBottom: 0,
    '& > *': {
      minWidth: 0,
    },
    '& $fieldInput, & $selectInput': {
      marginBottom: 0,
    },
    '& .MuiFormControl-root': {
      marginBottom: 0,
      marginTop: 0,
    },
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  profileAvatar: {
    width: 80,
    height: 80,
    fontSize: '1.25rem',
    fontWeight: 500,
    color: '#00205B',
    backgroundColor: '#E8F1FF',
    textTransform: 'uppercase',
    flexShrink: 0,
  },
  profilePhotoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginTop: 0,
  },
  profilePhotoActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  profilePhotoButtonRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  profilePhotoCaption: {
    fontSize: '0.8125rem',
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  checkboxField: {
    margin: 0,
    alignSelf: 'flex-start',
  },
  fieldError: {
    fontSize: '0.8125rem',
    lineHeight: 1.4,
    color: theme.palette.error.main,
    margin: 0,
  },
  selectionCard: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    border: '1px solid #E0E0E0',
    borderRadius: OUTLINED_INPUT_BORDER_RADIUS,
    backgroundColor: theme.palette.common.white,
    overflow: 'hidden',
    minWidth: 0,
  },
  selectionCardHeader: {
    padding: theme.spacing(2.5, 3),
    borderBottom: '1px solid #E0E0E0',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2, 2),
    },
  },
  selectionCardHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  selectionCardHeaderText: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    flex: '1 1 auto',
    minWidth: 0,
  },
  selectionIconCircle: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: '#EBF0F2',
    color: '#172024',
    '& .MuiSvgIcon-root': {
      display: 'block',
      fontSize: 16,
      lineHeight: 1,
    },
  },
  selectionCardTitle: {
    margin: 0,
    fontSize: '0.9375rem',
    fontWeight: 700,
    lineHeight: 1.2,
    color: '#172024',
    '&.MuiTypography-root': {
      margin: 0,
    },
  },
  selectionCardDescription: {
    margin: 0,
    marginTop: theme.spacing(0.25),
    fontSize: '0.8125rem',
    lineHeight: 1.3,
    color: theme.palette.text.secondary,
  },
  selectionCardError: {
    margin: 0,
    fontSize: '0.8125rem',
    lineHeight: 1.4,
    color: theme.palette.error.main,
  },
  selectionCardBody: {
    padding: theme.spacing(1.5, 3, 2),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5, 2, 1.75),
    },
  },
  selectionCardHeaderAction: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    color: '#00205B',
    padding: theme.spacing(0.5, 1),
    flexShrink: 0,
    minWidth: 'auto',
    minHeight: 'auto',
    height: 'auto',
    '& .MuiButton-label': {
      fontSize: '0.875rem',
      fontWeight: 600,
    },
    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(0.75),
      color: 'inherit',
    },
  },
  selectionCheckboxList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    width: '100%',
  },
  selectionCheckboxGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(1, 2),
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  selectionCheckboxField: {
    margin: 0,
    marginLeft: 0,
    marginRight: 0,
    alignItems: 'center',
    width: '100%',
  },
  selectionCheckboxLabel: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.25,
    letterSpacing: '0.01071em',
    color: '#172024',
  },
  selectionCheckboxRoot: {
    padding: theme.spacing(0.75),
    color: '#40484C',
    flexShrink: 0,
    '& svg': {
      fontSize: 20,
    },
  },
  selectionCheckboxChecked: {
    color: '#0053FF',
  },
  repeaterRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(2),
    width: '100%',
    '& > .MuiTextField-root': {
      flex: '1 1 auto',
      minWidth: 0,
      width: '100%',
    },
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      '& $repeaterDeleteButton, & $repeaterDeleteButtonFilled': {
        alignSelf: 'center',
        marginTop: theme.spacing(0.5),
      },
    },
  },
  testimonialFieldStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    flex: '1 1 auto',
    minWidth: 0,
    width: '100%',
  },
  repeaterDeleteButton: {
    padding: theme.spacing(1.25, 3),
    flexShrink: 0,
    textTransform: 'none',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    borderRadius: 9999,
    border: `1px solid ${OUTLINED_INPUT_BORDER}`,
    backgroundColor: theme.palette.common.white,
    color: '#172024',
    boxShadow: 'none',
    '&:hover': {
      borderColor: OUTLINED_INPUT_BORDER_HOVER,
      backgroundColor: theme.palette.common.white,
      boxShadow: 'none',
    },
    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(1),
      color: 'inherit',
    },
  },
  repeaterDeleteButtonFilled: {
    padding: theme.spacing(1.25, 3),
    flexShrink: 0,
    textTransform: 'none',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    borderRadius: 9999,
    border: 'none',
    color: '#172024',
    backgroundColor: '#EBF0F2',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#DDE4E7',
      boxShadow: 'none',
    },
    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(1),
      color: 'inherit',
    },
  },
}))

export const getFieldProps = classes => ({
  variant: 'outlined',
  fullWidth: true,
  margin: 'none',
  className: classes.fieldInput,
})

export const requiredLabel = label => `${label} *`
